import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Tabs, Tab, Row, Button, Col } from 'react-bootstrap';

import { FlightTicketApiService } from 'Services/ApiServices';
import { setLoader } from 'Store/Actions/Loading/LoadingAction';
import { toastr } from 'Helpers/ToastHelper';
import { getReactSelectStyles } from 'Helpers/UiHelper';
import { useTicketLock } from 'Hooks/useTicketLock';
import { useTicketPermissions } from 'Hooks/useTicketPermissions';
import { useDarkMode } from 'Hooks';
import { DS_F2W_Business_Travel_Team } from 'Constants/F2wRolesConstants';
import LoadingSpinner from 'Components/LoadingSpinner';

import FlyToWorkTicketInformationTab from 'Views/TicketProfile/Tabs/FlyToWorkTicketInformationTab';
import BusinessSupportTab from 'Views/TicketProfile/Tabs/BusinessSupportTab';
import BusinessTravelTeamTab from 'Views/TicketProfile/Tabs/BusinessTravelTeamTab';
import HistoryTab from 'Views/TicketProfile/Tabs/HistoryTab';
import AttachementsTab from 'Views/TicketProfile/Tabs/AttachementsTab';
import { validateNewStatusBeforeSubmit, validateConfirmedStatusBeforeSubmit } from './ticketValidation';
import BlockedPopup from './Components/BlockedPopup';
import ReminderPopup from './Components/ReminderPopup';
import StatusChangeDropdown from './Components/StatusChangeDropdown';

const TicketProfile = () => {
  const { status, ticketId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.currentUser);
  const userRoles = user?.roles;
  const { darkMode } = useDarkMode();

  const [flyToWorkTicketInfo, setFlyToWorkTicketInfo] = useState({});
  const [refreshComponent, setRefreshComponent] = useState(0);

  const {
    lockInfo,
    lockError,
    isLockRequired,
    isLockPending,
    hasLock,
    showBlockedPopup,
    showReminderPopup,
    resetLockTimers,
  } = useTicketLock(ticketId, user, flyToWorkTicketInfo?.status || status, userRoles, DS_F2W_Business_Travel_Team);

  const { isChangeStatusDropDownHidden, isBussinesTravelTeamTabHidden, isSubmitHidden } = useTicketPermissions(
    userRoles,
    flyToWorkTicketInfo?.status
  );

  const lockedBy = lockInfo?.lock?.lockedBy || lockInfo?.lockedBy || 'another user';

  const validFollowingStatus = [
    { value: 'pendingbtt', label: 'Pending Btt' },
    { value: 'pendingdes', label: 'Pending Des' },
    { value: 'confirmed', label: 'Confirmed' },
  ];

  const confirmedStatus = [
    { value: 'modified', label: 'modified' },
    { value: 'cancelled', label: 'cancelled' },
  ];

  const filteredStatus =
    flyToWorkTicketInfo?.status?.toLowerCase() === 'confirmed' && userRoles?.includes(DS_F2W_Business_Travel_Team)
      ? validFollowingStatus
      : validFollowingStatus.filter((status) => status.value !== flyToWorkTicketInfo?.status?.toLowerCase());

  useEffect(() => {
    if (!status || !ticketId) return;
    // Do not fetch the profile until the editing lock is confirmed for this
    // session. A user who is blocked (another user owns the lock) must never
    // download the ticket data, otherwise the block would be visual only and
    // would still leak the passenger's details (e.g. via the page title).
    if (!hasLock) return;
    dispatch(setLoader(true));
    FlightTicketApiService.getByStatusAndId(status, ticketId)
      .then((ticketInfo) => {
        if (!ticketInfo || !ticketInfo.ok) return;
        document.title = `${ticketInfo?.data.firstName} ${ticketInfo?.data.lastName} - Ticket profile`;
        setFlyToWorkTicketInfo(ticketInfo.data);
      })
      .finally(() => {
        dispatch(setLoader(false));
      });
  }, [status, ticketId, refreshComponent, dispatch, hasLock]);

  const getSubmitValidationError = () => {
    if (flyToWorkTicketInfo.status?.toLowerCase() === 'new') {
      return validateNewStatusBeforeSubmit(flyToWorkTicketInfo);
    }
    if (flyToWorkTicketInfo.status?.toLowerCase() === 'pendinghr') {
      return '';
    }
    if (!flyToWorkTicketInfo.followingStatus) {
      return 'Please select a status before submitting';
    }
    if (flyToWorkTicketInfo.followingStatus?.toLowerCase() === 'confirmed') {
      return validateConfirmedStatusBeforeSubmit(flyToWorkTicketInfo);
    }
    return '';
  };

  const handleSubmitChange = async () => {
    if (isLockRequired && !(await resetLockTimers())) return;

    const validationError = getSubmitValidationError();
    if (validationError) {
      toastr.error('Error', validationError);
      return;
    }

    toastr.confirm('Are you sure you want to submit this ticket?', {
      onOk: () => {
        dispatch(setLoader(true));
        const followingStatus =
          flyToWorkTicketInfo.status?.toLowerCase() === 'pendinghr' ? '' : flyToWorkTicketInfo.followingStatus;
        FlightTicketApiService.submitStatusChange(flyToWorkTicketInfo.id, {
          followingStatus,
          confirmedStatus: flyToWorkTicketInfo.confirmedStatus,
          isNTANSelected: flyToWorkTicketInfo.noFlightNeeded,
        })
          .then((response) => {
            if (!response || !response?.ok) {
              toastr.error('Error', response?.message);
              return;
            }
            if (response.warning) {
              toastr.warning('Warning', 'Status changed successfully, but email notifications could not be sent');
            }
            navigate(`/${response.data.status}/${response.data.id}`);
          })
          .finally(() => {
            dispatch(setLoader(false));
          });
      },
    });
  };

  const handleRemoveFlightTicket = async () => {
    if (isLockRequired && !(await resetLockTimers())) return;

    toastr.confirm('Are you sure you want to remove this ticket?', {
      onOk: () => {
        dispatch(setLoader(true));
        FlightTicketApiService.delete(flyToWorkTicketInfo.id)
          .then((response) => {
            if (!response || !response?.ok) {
              toastr.error('Error', response?.message);
              return;
            }
            const origin = window.location.origin;
            const url = `${origin}/all`;
            window.location.href = url;
          })
          .finally(() => {
            dispatch(setLoader(false));
          });
      },
    });
  };

  const getFollowingStatusOptions = () => {
    const isNoFlightNeeded =
      flyToWorkTicketInfo.noFlightNeeded === true ||
      flyToWorkTicketInfo.noFlightNeeded === 'true' ||
      flyToWorkTicketInfo.noFlightNeeded === 'Yes';

    if (isNoFlightNeeded && flyToWorkTicketInfo.status?.toLowerCase() === 'pendingdes') {
      return filteredStatus.filter((status) => status.value !== 'pendingbtt');
    }
    return filteredStatus;
  };

  return (
    <React.Fragment>
      {showBlockedPopup && (
        <BlockedPopup lockedBy={lockedBy} errorMessage={lockError} onReturn={() => (window.location.href = '/all')} />
      )}
      {isLockPending && <LoadingSpinner message="Securing ticket for editing..." fullScreen size="2x" />}
      {showReminderPopup && <ReminderPopup onContinue={resetLockTimers} />}
      {!showBlockedPopup && !isLockPending && (
        <>
          <Tabs
            defaultActiveKey={
              (window.location.hash ? window.location.hash.split('#')[1] : undefined) || 'businessSupport'
            }
            className="staff-profile-tabs"
          >
            <Tab eventKey="businessSupport" title="Operation Support">
              <BusinessSupportTab
                flyToWorkTicketInfo={flyToWorkTicketInfo}
                setRefreshComponent={setRefreshComponent}
                refreshComponent={refreshComponent}
                setFlyToWorkTicketInfo={setFlyToWorkTicketInfo}
                isLockRequired={isLockRequired}
                resetLockTimers={resetLockTimers}
              />
            </Tab>
            <Tab
              eventKey="businesstravelTeam"
              title="Business Travel Team"
              tabClassName={isBussinesTravelTeamTabHidden ? 'd-none' : ''}
            >
              <BusinessTravelTeamTab
                flyToWorkTicketInfo={flyToWorkTicketInfo}
                setRefreshComponent={setRefreshComponent}
                isLockRequired={isLockRequired}
                resetLockTimers={resetLockTimers}
              />
            </Tab>
            <Tab eventKey="attachements" title="Ticket attachements">
              <AttachementsTab
                flyToWorkTicketInfo={flyToWorkTicketInfo}
                setRefreshComponent={setRefreshComponent}
                refreshComponent={refreshComponent}
              />
            </Tab>
            <Tab
              eventKey="history"
              title="Ticket history"
              tabClassName={userRoles?.includes(DS_F2W_Business_Travel_Team) ? '' : 'd-none'}
            >
              <HistoryTab flyToWorkTicketInfo={flyToWorkTicketInfo} refreshComponent={refreshComponent} />
            </Tab>
            <Tab
              eventKey="flyToWorkTicketInfo"
              title="Ticket Info"
              tabClassName={userRoles?.includes(DS_F2W_Business_Travel_Team) ? '' : 'd-none'}
            >
              <FlyToWorkTicketInformationTab flyToWorkTicketInfo={flyToWorkTicketInfo} />
            </Tab>
          </Tabs>
          <Row className="mt-3 pb-2">
            <Col>
              {!isChangeStatusDropDownHidden && (
                <StatusChangeDropdown
                  flyToWorkTicketInfo={flyToWorkTicketInfo}
                  setFlyToWorkTicketInfo={setFlyToWorkTicketInfo}
                  confirmedStatus={confirmedStatus}
                  filteredStatus={filteredStatus}
                  getFollowingStatusOptions={getFollowingStatusOptions}
                  darkMode={darkMode}
                  getReactSelectStyles={getReactSelectStyles}
                />
              )}
              <div className="d-flex justify-content-end gap-2">
                {userRoles?.includes(DS_F2W_Business_Travel_Team) && (
                  <>
                    <Button variant="danger" size="sm" onClick={handleRemoveFlightTicket}>
                      Delete
                    </Button>
                  </>
                )}
                {!isSubmitHidden && (
                  <Button variant="info" size="sm" onClick={handleSubmitChange}>
                    Submit
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        </>
      )}
    </React.Fragment>
  );
};

export default TicketProfile;
